import { useState, useRef, useEffect } from "react";
import {
  SHORT_LINK_ERROR_KINDS,
  SHORT_LINK_FALLBACK_ERROR_KINDS,
  resolveTrivvyShortUrl,
  validateTrivvyShortUrl,
} from "../utils/trivvyShortLink";

export const SHORT_LINK_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
};

// TBV-938: resolves one item's permanent short URL for one sharing surface
// (Copy modal, list dialog, QR, WhatsApp). Shared so every surface gets the
// same loading / stale-response / retry / rate-limit behavior.
// - Resolves only while `enabled` (e.g. its modal is open) — never per render.
// - A late response for a closed surface or another item is ignored, and the
//   in-flight request is aborted.
// - `knownShortUrl` (a list row's server-built URL) skips the request.
// - `canOfferFallback` is true only for transient failures (server/offline);
//   auth, permission, missing-item, rate-limit and config errors never are.
const useTrivvyShortLink = ({
  itemType,
  itemId,
  source,
  enabled = true,
  knownShortUrl,
  onResolved,
}) => {
  const [linkStatus, setLinkStatus] = useState(SHORT_LINK_STATUS.IDLE);
  const [resolvedLink, setResolvedLink] = useState(null);
  const [linkErrorKind, setLinkErrorKind] = useState(null);
  const [isRetryBlocked, setIsRetryBlocked] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const requestVersionRef = useRef(0);
  const isMountedRef = useRef(true);
  const retryTimerRef = useRef(null);
  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(retryTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    setResolvedLink(null);
    setLinkErrorKind(null);

    if (!enabled || !itemId) {
      setLinkStatus(SHORT_LINK_STATUS.IDLE);
      return undefined;
    }

    // A list row may already carry its server-built URL — no request needed.
    if (retryCount === 0 && validateTrivvyShortUrl(knownShortUrl, itemType, source)) {
      setResolvedLink({ itemId, source, shortUrl: knownShortUrl });
      setLinkStatus(SHORT_LINK_STATUS.READY);
      return undefined;
    }

    setLinkStatus(SHORT_LINK_STATUS.LOADING);
    const abortController =
      typeof AbortController !== "undefined" ? new AbortController() : null;

    resolveTrivvyShortUrl({
      itemType,
      itemId,
      source,
      signal: abortController ? abortController.signal : undefined,
    })
      .then((linkData) => {
        if (requestVersionRef.current !== requestVersion) {
          return;
        }
        setResolvedLink({ itemId, source, shortUrl: linkData.shortUrl });
        setLinkStatus(SHORT_LINK_STATUS.READY);
        if (onResolvedRef.current) {
          onResolvedRef.current(linkData.shortUrl);
        }
      })
      .catch((error) => {
        if (
          requestVersionRef.current !== requestVersion ||
          error.kind === SHORT_LINK_ERROR_KINDS.CANCELLED
        ) {
          return;
        }
        setLinkErrorKind(error.kind);
        setLinkStatus(SHORT_LINK_STATUS.ERROR);
        if (error.kind === SHORT_LINK_ERROR_KINDS.RATE_LIMITED && error.retryAfterSeconds) {
          setIsRetryBlocked(true);
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              setIsRetryBlocked(false);
            }
          }, error.retryAfterSeconds * 1000);
        }
      });

    return () => {
      // Invalidate and cancel on close / item or surface switch.
      requestVersionRef.current += 1;
      if (abortController) {
        abortController.abort();
      }
    };
  }, [itemType, itemId, source, enabled, retryCount]);

  // Only a link resolved for THIS item and surface is ever exposed.
  const shortUrl =
    linkStatus === SHORT_LINK_STATUS.READY &&
    resolvedLink &&
    resolvedLink.itemId === itemId &&
    resolvedLink.source === source
      ? resolvedLink.shortUrl
      : "";

  return {
    linkStatus,
    shortUrl,
    linkErrorKind,
    isRetryBlocked,
    canOfferFallback:
      linkStatus === SHORT_LINK_STATUS.ERROR &&
      SHORT_LINK_FALLBACK_ERROR_KINDS.indexOf(linkErrorKind) !== -1,
    retry: () => setRetryCount((count) => count + 1),
  };
};

export default useTrivvyShortLink;
